/**
 * Toast Component
 *
 * Individual toast notification with auto-dismiss, actions, and animations.
 * Uses theme colors, design tokens, and icon system for consistency.
 */

import { useEffect, useState } from 'react';
import {
  Toast as ToastType,
  ToastType as ToastVariant,
} from '@/types/toast.types';
import { Icon, IconName } from '@/icons';
import { useThemeColors } from '@/styles';
import { radius, shadows } from '@/styles/design-tokens';
import { cn } from '@/lib/utils';

interface ToastProps {
  toast: ToastType;
  onDismiss: (id: string) => void;
}

/**
 * Get toast configuration based on type
 */
function getToastConfig(
  type: ToastVariant,
  colors: ReturnType<typeof useThemeColors>
) {
  switch (type) {
    case ToastVariant.SUCCESS:
      return {
        icon: IconName.CHECK,
        iconColor: colors.success,
        iconBgColor: `${colors.success}20`,
        borderColor: `${colors.success}30`,
        progressColor: colors.success,
      };
    case ToastVariant.ERROR:
      return {
        icon: IconName.ALERT_CIRCLE,
        iconColor: colors.error,
        iconBgColor: `${colors.error}20`,
        borderColor: `${colors.error}30`,
        progressColor: colors.error,
      };
    case ToastVariant.LOADING:
      return {
        icon: IconName.LOADER,
        iconColor: colors.blue,
        iconBgColor: `${colors.blue}20`,
        borderColor: `${colors.blue}30`,
        progressColor: colors.blue,
      };
    case ToastVariant.INFO:
      return {
        icon: IconName.INFO,
        iconColor: colors.blue,
        iconBgColor: `${colors.blue}20`,
        borderColor: `${colors.blue}30`,
        progressColor: colors.blue,
      };
    case ToastVariant.WARNING:
      return {
        icon: IconName.ALERT_CIRCLE,
        iconColor: colors.rust, // Using rust (amber/orange) for warnings
        iconBgColor: `${colors.rust}20`,
        borderColor: `${colors.rust}30`,
        progressColor: colors.rust,
      };
  }
}

/**
 * Toast Component
 */
export function Toast({ toast, onDismiss }: ToastProps) {
  const colors = useThemeColors();
  const config = getToastConfig(toast.type, colors);
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  // Handle auto-dismiss countdown
  useEffect(() => {
    if (
      toast.duration === null ||
      toast.duration === undefined ||
      toast.duration <= 0
    )
      return;

    const totalDuration = toast.duration;

    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - toast.created_at;
      const remaining = Math.max(0, totalDuration - elapsed);
      const newProgress = (remaining / totalDuration) * 100;

      setProgress(newProgress);

      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 50); // Update every 50ms for smooth animation

    return () => clearInterval(interval);
  }, [toast.created_at, toast.duration]);

  /**
   * Handle dismiss with exit animation
   */
  const handleDismiss = () => {
    setIsExiting(true);
    // Wait for animation to complete before removing
    setTimeout(() => {
      onDismiss(toast.id);
    }, 200); // Match animation duration
  };

  /**
   * Handle action button click
   */
  const handleAction = () => {
    toast.action?.onClick();
    handleDismiss();
  };

  return (
    <div
      className={cn(
        'group relative flex items-start gap-3 p-4 pr-10',
        'transition-all duration-200 ease-out',
        'pointer-events-auto',
        isExiting
          ? 'opacity-0 translate-x-8 scale-95'
          : 'opacity-100 translate-x-0 scale-100 hover:scale-[1.02]'
      )}
      style={{
        minWidth: 'calc(320px * var(--sizing-multiplier, 1))',
        maxWidth: 'calc(420px * var(--sizing-multiplier, 1))',
        backgroundColor: colors.background,
        borderRadius: radius.lg,
        border: `1px solid ${config.borderColor}`,
        boxShadow: shadows['soft-lg'],
      }}
      role="alert"
      aria-live="polite"
    >
      {/* Icon */}
      <div
        className="flex-shrink-0 rounded-full p-2"
        style={{ backgroundColor: config.iconBgColor }}
      >
        <Icon
          name={config.icon}
          size="sm"
          color={config.iconColor}
          className={toast.type === ToastVariant.LOADING ? 'animate-spin' : ''}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-0.5">
        {/* Title */}
        <p
          className="text-sm font-semibold leading-snug"
          style={{ color: colors.text.primary }}
        >
          {toast.title}
        </p>

        {/* Message */}
        {toast.message && (
          <p
            className="text-sm leading-snug mt-1"
            style={{ color: colors.text.secondary }}
          >
            {toast.message}
          </p>
        )}

        {/* Action Button */}
        {toast.action && (
          <button
            onClick={handleAction}
            className="text-sm font-medium mt-2 hover:underline transition-all"
            style={{ color: config.iconColor }}
          >
            {toast.action.label}
          </button>
        )}
      </div>

      {/* Close Button */}
      <button
        onClick={handleDismiss}
        className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ color: colors.text.tertiary }}
        aria-label="Dismiss notification"
      >
        <Icon name={IconName.X} size="sm" />
      </button>

      {/* Progress Bar (for auto-dismiss) */}
      {toast.duration !== null &&
        toast.duration !== undefined &&
        toast.duration > 0 && (
          <div
            className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden"
            style={{
              borderBottomLeftRadius: radius.lg,
              borderBottomRightRadius: radius.lg,
            }}
          >
            <div
              className="h-full transition-all duration-50 ease-linear"
              style={{
                width: `${progress}%`,
                backgroundColor: config.progressColor,
                opacity: 0.6,
              }}
            />
          </div>
        )}
    </div>
  );
}
